import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag, Music, BookOpen, ExternalLink, Home, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const presetAmounts = [5, 10, 25, 50, 100];

const supportLinks = [
  {
    title: "Shop Merch",
    description: "Get apparel, plushies, and CocoaLife™ supplements",
    icon: ShoppingBag,
    link: "/shop",
    internal: true,
    color: "secondary"
  },
  {
    title: "Buy the Book",
    description: "Oumaumau and the Star Sung Compass on Amazon Kindle",
    icon: BookOpen,
    link: "https://www.amazon.com/vdp/0ce1e9c32b2a44af8f885a7928faf536?aci=amzn1.vse.video.0ce1e9c32b2a44af8f885a7928faf536&product=B0FW7QRFJQ&ref=cm_sw_cp_r_ib_dt_klgCIDXxuI9qf",
    internal: false,
    color: "accent"
  },
  {
    title: "Listen to Music",
    description: "Stream GradTrax on Spotify - 60+ original songs",
    icon: Music,
    link: "https://open.spotify.com/artist/2m8OD1Y5PSQmd5mBPQvR3r",
    internal: false,
    color: "secondary"
  }
];

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);

  useEffect(() => {
    // Check for success or canceled params
    if (searchParams.get('success') === 'true') {
      setShowSuccess(true);
    } else if (searchParams.get('canceled') === 'true') {
      setShowCanceled(true);
    }
  }, [searchParams]);

  const handlePresetClick = (presetAmount) => {
    setAmount(presetAmount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value && !isNaN(parseFloat(value))) {
      setAmount(parseFloat(value));
    }
  };

  const handleDonate = async () => {
    if (amount < 1) {
      toast.error('Minimum donation amount is $1');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/donate/create-session`, {
        amount: amount,
        donor_name: donorName || 'Anonymous',
        donor_email: donorEmail || null,
        message: message || null,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process donation');
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="donate-page">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Thank You!
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Your donation has been received. Your support helps create more satirical content, music, and educational resources.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/publications">
                <Button className="btn-primary gap-2">
                  <BookOpen className="w-4 h-4" />
                  View Publications
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showCanceled) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="donate-page">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-muted-foreground" />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Donation Canceled
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              No worries! Feel free to donate whenever you're ready.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => setShowCanceled(false)} 
                className="btn-primary gap-2"
              >
                <Heart className="w-4 h-4" />
                Try Again
              </Button>
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="donate-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-accent" />
          </div>
          <h1 className="section-header mb-4">Support the Mission</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            Your support helps create more satirical content, music, and educational resources. 
            Every contribution makes a difference.
          </p>
        </motion.div>

        {/* Donation Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto mb-16"
        >
          <Card className="bg-card border-accent/30" style={{ boxShadow: '0 0 40px rgba(244, 196, 48, 0.15)' }}>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>
                Make a Donation
              </h2>

              {/* Preset Amounts */}
              <div className="mb-6">
                <Label className="text-sm text-muted-foreground mb-3 block">Select Amount</Label>
                <div className="grid grid-cols-5 gap-2">
                  {presetAmounts.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant={amount === preset && !customAmount ? "default" : "outline"}
                      className={`h-12 ${amount === preset && !customAmount ? 'bg-accent text-accent-foreground hover:bg-accent/90' : ''}`}
                      onClick={() => handlePresetClick(preset)}
                      data-testid={`donate-preset-${preset}`}
                    >
                      ${preset}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="mb-6">
                <Label htmlFor="customAmount" className="text-sm text-muted-foreground mb-2 block">
                  Or Enter Custom Amount
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="customAmount"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    className="pl-8"
                    data-testid="donate-custom-amount"
                  />
                </div>
              </div>

              {/* Donor Info (Optional) */}
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="donorName" className="text-sm text-muted-foreground mb-2 block">
                    Name (Optional)
                  </Label>
                  <Input
                    id="donorName"
                    type="text"
                    placeholder="Your name"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    data-testid="donate-name"
                  />
                </div>
                <div>
                  <Label htmlFor="donorEmail" className="text-sm text-muted-foreground mb-2 block">
                    Email (Optional - for receipt)
                  </Label>
                  <Input
                    id="donorEmail"
                    type="email"
                    placeholder="your@email.com"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    data-testid="donate-email"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm text-muted-foreground mb-2 block">
                    Message (Optional)
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Leave a message of support..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    data-testid="donate-message"
                  />
                </div>
              </div>

              {/* Donate Button */}
              <Button
                className="w-full h-14 text-lg btn-primary"
                onClick={handleDonate}
                disabled={isLoading || amount < 1}
                data-testid="donate-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5 mr-2" />
                    Donate ${amount.toFixed(2)}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Secure payment powered by Stripe. All donations support content creation.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stone Initiative Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center mb-16"
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Stone Initiative Donations
              </h3>
              <p className="text-muted-foreground mb-6">
                All donations to Stone initiatives will be invested until disbursal. 
                Your contribution helps fund community projects and new content creation.
              </p>
              <Link to="/stone-initiative">
                <Button variant="outline" className="gap-2">
                  Learn About the Stone Initiative
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Make a Purchase Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            Or Support by Making a Purchase
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {supportLinks.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {item.internal ? (
                  <Link to={item.link}>
                    <Card className="bg-card border-border/50 hover:border-secondary/50 transition-all h-full card-hover">
                      <CardContent className="p-6 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                          <item.icon className="w-7 h-7 text-secondary" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ) : (
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    <Card className="bg-card border-border/50 hover:border-accent/50 transition-all h-full card-hover group">
                      <CardContent className="p-6 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-4">
                          <item.icon className="w-7 h-7 text-accent" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 flex items-center justify-center gap-2">
                          {item.title}
                          <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </h3>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      </CardContent>
                    </Card>
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/publications">
            <Button variant="outline" className="gap-2">
              <BookOpen className="w-4 h-4" />
              View Publications
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
